import React from 'react';
import { FileText, Globe, BarChart3, Shield, Cpu, TrendingUp, Layers, Database, Zap, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import KineticHero from '@/components/KineticHero';

const DOCS_CONTENT = `
## What It Is
**Sovereign IQ** is an AI-powered country intelligence and sovereign risk assessment platform. It generates investment-grade country blueprints across 115+ industry sectors, combining real-time World Bank and IMF economic data with institutional-grade AI analysis from our intelligence engine.

The platform is designed for investment professionals, development finance institutions, multinational corporations, consulting firms, and government agencies evaluating market entry into emerging and frontier economies.

## Platform Architecture

| Layer | Technology |
|-------|-----------|
| Frontend | React 18, Tailwind CSS, Shadcn/UI, Recharts, react-simple-maps |
| Backend | Python FastAPI, Motor (async MongoDB), httpx |
| AI Engine | AI engine (text), Gemini Nano Banana (images), Sora 2 (video), OpenAI TTS (audio) |
| Live Data | World Bank API (8 indicators), IMF WEO (5 forecasts, 122 countries), auto-refreshing |
| PDF Engine | ReportLab (investment-grade, 30-60 page reports with custom cover, economic trends, illustrations) |
| Research DB | 1,000+ curated sources across 20 categories with 145 API integrations |
| Database | MongoDB |
| Auth | JWT-based authentication. Public pages + protected intelligence platform |
`;

const SECTIONS_DATA = [
  { num: 0, name: 'Country Snapshot', desc: 'Key indicators, rankings, GDP, population, HDI, credit rating' },
  { num: 1, name: 'Executive Summary', desc: 'Country overview, sector status, strategic recommendation' },
  { num: 2, name: 'PESTLE Analysis', desc: 'Political, Economic, Social, Technology, Legal, Environmental' },
  { num: 3, name: 'Market Opportunity', desc: 'Demand analysis, TAM/SAM/SOM, service gaps' },
  { num: 4, name: 'Government Capacity', desc: 'Payment mechanisms, budget, fiscal risk' },
  { num: 5, name: 'Competitive Landscape', desc: 'Operators, market structure, entry barriers' },
  { num: 6, name: 'Business Model', desc: 'Entry model, revenue streams, phased rollout' },
  { num: 7, name: 'Risk Analysis', desc: 'Political, economic, operational risks & mitigation' },
  { num: 8, name: 'Financial Projections', desc: 'CAPEX, revenue forecast, IRR, NPV, payback' },
  { num: 9, name: 'Sovereign Risk', desc: 'Credit ratings, debt sustainability, macro stability' },
  { num: 10, name: 'Social Impact', desc: 'Employment, SDG alignment, ESG scoring' },
  { num: 11, name: 'Governance', desc: 'Institutional framework, regulatory quality, PPP readiness' },
];

const AI_STUDIO = [
  { name: 'Audio Overview', tech: 'OpenAI TTS', output: '2-4 min professional narration MP3' },
  { name: 'Quick Video', tech: 'Sora 2', output: '8-second cinematic preview' },
  { name: 'Full Narrated Video', tech: 'Sora 2 + TTS + FFmpeg', output: 'Multi-scene documentary with audio' },
  { name: 'Infographic', tech: 'Gemini Nano Banana', output: 'One-page investment infographic' },
  { name: 'AI Illustrations', tech: 'Gemini Nano Banana', output: 'Per-section visual with watermark' },
];

const WB_INDICATORS = [
  { name: 'GDP Growth', code: 'NY.GDP.MKTP.KD.ZG', coverage: 'All countries' },
  { name: 'Inflation', code: 'FP.CPI.TOTL.ZG', coverage: 'Most countries' },
  { name: 'Unemployment', code: 'SL.UEM.TOTL.ZS', coverage: 'All countries' },
  { name: 'FDI Net Inflows', code: 'BN.KLT.DINV.CD', coverage: 'All countries' },
  { name: 'Current Account', code: 'BN.CAB.XOKA.CD', coverage: 'All countries' },
  { name: 'Trade/GDP', code: 'NE.TRD.GNFS.ZS', coverage: 'Most countries' },
  { name: 'Govt Debt/GDP', code: 'GC.DOD.TOTL.GD.ZS', coverage: 'Limited' },
  { name: 'External Debt/GNI', code: 'DT.DOD.DECT.GN.ZS', coverage: '11 countries' },
];

const RISK_RULES = [
  { priority: '1st', source: 'Credit Rating', stable: 'BBB- or above', caution: 'BB range', moderate: 'B range', high: 'CCC or below' },
  { priority: '2nd', source: 'HDI Score', stable: '>= 0.75', caution: '0.55 - 0.74', moderate: '—', high: '< 0.55' },
  { priority: '3rd', source: 'Sovereign Score', stable: '>= 7/10', caution: '4 - 6.9', moderate: '—', high: '< 4' },
];

const API_ENDPOINTS = [
  { method: 'GET', path: '/api/countries', purpose: 'List all countries' },
  { method: 'POST', path: '/api/countries', purpose: 'Add new country' },
  { method: 'GET', path: '/api/countries/:id', purpose: 'Get country details' },
  { method: 'DELETE', path: '/api/countries/:id', purpose: 'Delete country' },
  { method: 'POST', path: '/api/countries/:id/generate-section/:name', purpose: 'Generate AI section' },
  { method: 'POST', path: '/api/countries/:id/generate-illustration/:name', purpose: 'Generate AI illustration' },
  { method: 'GET', path: '/api/countries/:id/blueprint/pdf', purpose: 'Download Investment-Grade PDF' },
  { method: 'POST', path: '/api/countries/:id/generate-references', purpose: 'Generate numbered references' },
  { method: 'POST', path: '/api/countries/:id/generate-audio-overview', purpose: 'Generate TTS audio' },
  { method: 'POST', path: '/api/countries/:id/generate-infographic', purpose: 'Generate Gemini infographic' },
  { method: 'POST', path: '/api/countries/:id/generate-full-video', purpose: 'Generate Sora 2 video' },
  { method: 'GET', path: '/api/intelligence-feed', purpose: 'World Bank live feed (40+ items)' },
  { method: 'GET', path: '/api/wb-trends/:indicator', purpose: 'Historical 6-year trend data' },
  { method: 'GET', path: '/api/countries/:id/wb-indicators', purpose: 'Country-specific WB data' },
  { method: 'GET', path: '/api/sectors', purpose: 'List 115 sectors' },
];

const PAGES = [
  { name: 'Country Intelligence Dashboard', path: '/', desc: 'Interactive SVG map, live intelligence feed (40+ indicators), historical trend charts, key targets, system status bar' },
  { name: 'Intelligence Targets', path: '/countries', desc: 'Full grid of tracked countries with real metrics (GDP Growth, Credit Rating, HDI), risk badges, search filter, add territory' },
  { name: 'Blueprint Configuration', path: '/blueprint-config', desc: '6 focus areas (Economy, Security, Infrastructure, Diplomacy, Technology, Climate), step indicator, project codename' },
  { name: 'Section Generator', path: '/generator', desc: '12 section cards with Generate/Regenerate, content preview, Generate All Sections, status badges' },
  { name: 'Detail Project View', path: '/countries/:id/blueprint', desc: 'Full report: hero banner, sidebar index, economic trends, 12 sections, AI Studio, PDF export' },
  { name: 'Country Comparison', path: '/comparison', desc: 'Side-by-side analysis of 2-5 countries across 7 dimensions' },
];

function PlatformDocsPage() {
  return (
    <div data-testid="docs-page" className="min-h-screen bg-[#f8f9ff] text-[#0b1c30]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <KineticHero
        uid="docs"
        eyebrow="Documentation · architecture · APIs"
        title="Platform"
        accent="Summary"
        subtitle="Complete reference for Sovereign IQ — architecture, features, APIs, and the 1,000+ data sources powering every country blueprint."
      />
      <div className="p-6 lg:p-8">

      <div className="max-w-[1100px] space-y-8">
        {/* What It Is */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <div className="section-content prose prose-slate max-w-none prose-headings:text-[#0b1c30] prose-p:text-[#434655] prose-table:border prose-td:border prose-td:px-3 prose-td:py-2 prose-th:border prose-th:px-3 prose-th:py-2 prose-th:bg-[#1B2A3D] prose-th:text-white">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{DOCS_CONTENT}</ReactMarkdown>
          </div>
        </section>

        {/* Pages */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <Layers className="w-5 h-5 text-[#00A651]" /> Pages & Features
          </h2>
          <div className="space-y-3">
            {PAGES.map((page, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-[#f8f9ff] rounded-lg">
                <span className="text-xs font-bold text-[#00A651] bg-[#00A651]/10 px-2 py-1 rounded shrink-0">{page.path}</span>
                <div>
                  <h3 className="text-sm font-bold text-[#0b1c30]">{page.name}</h3>
                  <p className="text-xs text-[#434655] mt-0.5 leading-relaxed">{page.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 12 Blueprint Sections */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#00A651]" /> 12 Blueprint Sections
          </h2>
          <p className="text-xs text-[#434655] mb-4">Each section is generated independently by our AI intelligence engine acting as a senior partner & chief economist with 50 years of experience, following the Pyramid Principle.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {SECTIONS_DATA.map(s => (
              <div key={s.num} className="flex items-start gap-3 p-3 bg-[#f8f9ff] rounded-lg">
                <span className="text-xs font-bold text-[#004ac6] bg-[#e5eeff] w-7 h-7 rounded flex items-center justify-center shrink-0">{s.num}</span>
                <div>
                  <h4 className="text-xs font-bold text-[#0b1c30]">{s.name}</h4>
                  <p className="text-[10px] text-[#434655] mt-0.5">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* AI Studio */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#00A651]" /> AI Studio
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#1B2A3D] text-white">
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Feature</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Technology</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Output</th>
                </tr>
              </thead>
              <tbody>
                {AI_STUDIO.map((item, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8f9ff]'}>
                    <td className="p-3 font-semibold text-[#0b1c30]">{item.name}</td>
                    <td className="p-3 text-[#004ac6]">{item.tech}</td>
                    <td className="p-3 text-[#434655]">{item.output}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* PDF Export */}
        <section className="bg-[#1B2A3D] text-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#00A651]" /> PDF Export (Investment-Grade Format)
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[
              { label: 'Pages', value: '30-40' },
              { label: 'Format', value: 'Institutional style' },
              { label: 'Accent', value: '#00A651' },
              { label: 'Charts', value: '4 types' },
            ].map((m, i) => (
              <div key={i} className="bg-white/5 p-3 rounded-lg">
                <span className="text-[9px] text-white/40 uppercase tracking-widest">{m.label}</span>
                <p className="text-base font-bold mt-1">{m.value}</p>
              </div>
            ))}
          </div>
          <ul className="text-xs text-white/70 space-y-1.5 list-disc list-inside">
            <li>Cover page: left-aligned country name, sector, region, date</li>
            <li>Table of Contents with numbered sections</li>
            <li>Each section starts on a new page with green header bar + source attribution</li>
            <li>Data Visualizations: Revenue Forecast, Risk Matrix, Market Sizing, Sovereign Risk radar</li>
            <li>Infographic page, Numbered References (30-40 entries), Back page</li>
          </ul>
        </section>

        {/* World Bank Integration */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <Database className="w-5 h-5 text-[#00A651]" /> World Bank Integration (Live Data)
          </h2>
          <p className="text-xs text-[#434655] mb-4">8 indicators fetched in real-time from World Bank API, cached on startup. Historical trends: 6-year time series (2019-2024).</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#1B2A3D] text-white">
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Indicator</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">API Code</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Coverage</th>
                </tr>
              </thead>
              <tbody>
                {WB_INDICATORS.map((ind, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8f9ff]'}>
                    <td className="p-3 font-semibold text-[#0b1c30]">{ind.name}</td>
                    <td className="p-3 font-mono text-[10px] text-[#004ac6]">{ind.code}</td>
                    <td className="p-3 text-[#434655]">{ind.coverage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Risk Evaluation */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#00A651]" /> Risk Evaluation Logic
          </h2>
          <p className="text-xs text-[#434655] mb-4">Risk assessment uses real extracted data from AI-generated content — not arbitrary scores.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#1B2A3D] text-white">
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Priority</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Source</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px] text-blue-300">Stable</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px] text-yellow-300">Caution</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px] text-orange-300">Moderate</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px] text-red-300">High Risk</th>
                </tr>
              </thead>
              <tbody>
                {RISK_RULES.map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8f9ff]'}>
                    <td className="p-3 font-bold text-[#004ac6]">{r.priority}</td>
                    <td className="p-3 font-semibold text-[#0b1c30]">{r.source}</td>
                    <td className="p-3 text-blue-600">{r.stable}</td>
                    <td className="p-3 text-yellow-600">{r.caution}</td>
                    <td className="p-3 text-orange-600">{r.moderate}</td>
                    <td className="p-3 text-red-600">{r.high}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* API Endpoints */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <Cpu className="w-5 h-5 text-[#00A651]" /> API Endpoints
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-[#1B2A3D] text-white">
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Method</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Endpoint</th>
                  <th className="text-left p-3 font-bold uppercase tracking-wider text-[10px]">Purpose</th>
                </tr>
              </thead>
              <tbody>
                {API_ENDPOINTS.map((ep, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f8f9ff]'}>
                    <td className="p-3">
                      <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${
                        ep.method === 'GET' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>{ep.method}</span>
                    </td>
                    <td className="p-3 font-mono text-[10px] text-[#0b1c30]">{ep.path}</td>
                    <td className="p-3 text-[#434655]">{ep.purpose}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 1,000+ Research Sources */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-2 flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#00A651]" /> 1,000+ Authoritative Data Sources
          </h2>
          <p className="text-xs text-[#434655] mb-4">All AI prompts dynamically reference Critical and High priority sources from our curated research database across 20 categories with 145 API integrations.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {[
              { cat: 'Multilateral & Macro Data', count: 48, examples: 'IMF, World Bank, UNCTAD, OECD, AfDB, UN Data, ADB' },
              { cat: 'Risk, Governance, Legal & Compliance', count: 50, examples: 'Fitch, Moody\'s, S&P, EIU, Fragile States Index, TI, PRS/ICRG' },
              { cat: 'Trade, Logistics, Infrastructure & PPP', count: 50, examples: 'ITC Trade Map, WTO, UN Comtrade, WITS, AfCFTA, PPP Knowledge Lab' },
              { cat: 'Climate, ESG, Environment & Disaster', count: 50, examples: 'IEA, IRENA, Climate Watch, UNEP, CDP, SASB, Global Carbon Atlas' },
              { cat: 'Sector Intelligence: Energy, Mining, Ag, Tech', count: 50, examples: 'ITU, WIPO, GSMA, WHO, UNIDO, OPEC, EITI' },
              { cat: 'Investment Promotion & Business Environment', count: 50, examples: 'WB B-READY, Heritage Foundation, Fraser Institute, WEF' },
              { cat: 'Africa Regional Institutions', count: 50, examples: 'AfDB, AU, AUDA-NEPAD, Mo Ibrahim, Africa CDC, ACLED' },
              { cat: 'FinTech, InsurTech, Payments & Carbon Markets', count: 50, examples: 'World Bank Findex, fDi Intelligence, AVCA, Verra, Gold Standard' },
              { cat: 'GovTech, LegalTech, ESG Reporting & Biodiversity', count: 48, examples: 'PwC Tax, Deloitte, KPMG, Lexology, TNFD, SBTN' },
              { cat: 'Health, Biotech, Pharma & Education', count: 50, examples: 'WHO, ILO, UNDP, UNESCO, Global Health Observatory' },
              { cat: 'National Statistics Offices & Census', count: 50, examples: 'KNBS, NBS Nigeria, Stats SA, INE, BPS Indonesia' },
              { cat: 'Open Data, Alternative Signals & Research', count: 50, examples: 'GDELT, OpenCorporates, Crunchbase, Google Trends' },
              { cat: 'Energy Transition, Power, Oil & Gas, Hydrogen', count: 49, examples: 'IRENA, IEA, OPEC, EITI, Hydrogen Council' },
              { cat: 'Mining, Critical Minerals, CCS & Methane', count: 49, examples: 'USGS, BGS, S&P Global Market Intelligence, CCUS Hub' },
              { cat: 'Digital, Telecom, AI, Cyber & Data Centers', count: 49, examples: 'ITU, GSMA, ENISA, Cloudflare Radar, Submarine Cable Map' },
              { cat: 'Construction, Smart Cities & PropTech', count: 49, examples: 'Global Infrastructure Hub, APMG, LEED, BIM Forum' },
              { cat: 'Transport, Shipping, Aviation & Ports', count: 49, examples: 'IATA, IMO, UNCTAD Review of Maritime, FlightRadar24' },
              { cat: 'Agriculture, Food, Water & Circular Economy', count: 50, examples: 'FAO, AQUASTAT, World Resources Institute, Ellen MacArthur' },
              { cat: 'Media, Gaming, Sports, Tourism & Events', count: 50, examples: 'UNWTO, Newzoo, PwC Entertainment, Statista' },
              { cat: 'Business, Geopolitics & Market Intelligence', count: 50, examples: 'CIA Factbook, Chatham House, Brookings, IISS, Stratfor' },
            ].map((c, i) => (
              <div key={i} className="p-3 bg-[#f8f9ff] rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="text-xs font-bold text-[#0b1c30]">{c.cat}</h4>
                  <span className="text-[9px] font-bold text-[#004ac6] bg-[#e5eeff] px-1.5 py-0.5 rounded">{c.count}</span>
                </div>
                <p className="text-[10px] text-[#434655]">{c.examples}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5">
            {['World Bank', 'IMF', 'UNCTAD', 'OECD', 'AfDB', 'UN Data', 'Transparency International',
              'Heritage Foundation', 'WIPO', 'WEF', 'ITC Trade Map', 'WTO', 'UN Comtrade', 'WITS',
              'Fitch Ratings', "Moody's", 'S&P Global', 'EIU', 'Fragile States Index', 'PRS Group',
              'IEA', 'IRENA', 'Climate Watch', 'UNEP', 'ILO', 'UNDP', 'UNESCO',
              'World Bank Findex', 'GSMA', 'fDi Intelligence', 'PwC Tax', 'Deloitte', 'KPMG',
              'Lexology', 'ITU', 'WHO', 'UNIDO', 'PPP Knowledge Lab', 'Global Infrastructure Hub',
              'CIA World Factbook', 'Mo Ibrahim Foundation', 'Chatham House', 'Brookings',
              'ACLED', 'GDELT', 'CEIC Data', 'AfCFTA', 'CDP', 'SASB', 'IISS'
            ].map((source, i) => (
              <span key={i} className="text-[10px] font-medium text-[#434655] bg-[#f8f9ff] px-2 py-1 rounded-lg border border-[#c3c6d7]/20">
                {source}
              </span>
            ))}
          </div>
        </section>

        {/* 100 Sectors */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#0b1c30] tracking-tight mb-5 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#00A651]" /> 115 Industry Sectors
          </h2>
          <div className="flex flex-wrap gap-2">
            {['Waste Management', 'Renewable Energy', 'Healthcare', 'FinTech', 'Agriculture', 'Mining',
              'Telecom', 'Water & Sanitation', 'Education', 'Construction', 'Transport', 'Oil & Gas',
              'Manufacturing', 'Retail', 'Tourism', 'Food Processing', 'Textiles', 'Automotive',
              'Aerospace', 'Chemicals', 'Cement', 'Steel', 'Forestry', 'Fisheries',
              'Power Generation', 'Smart Cities', 'InsurTech', 'Cybersecurity', 'AI & Robotics',
              'Blockchain', 'Media', 'Sports', 'Legal Services', 'Consulting', 'Shipping',
              'Aviation', 'Rail', 'Ports', 'Warehousing', 'Plastic Recycling',
              'Carbon Credits', 'Microfinance', 'Social Enterprise', 'Beauty',
              'Consumer Electronics', 'Packaging', 'Security', 'Biotech', 'Space Tech', 'Nuclear',
              'Electric Vehicles', 'Hydrogen & Green Fuels', 'Digital Health', 'Precision Agriculture',
              'Rare Earth Minerals', 'Semiconductors', 'Quantum Computing', 'Drone Tech',
              '3D Printing', 'Nanotechnology', 'PropTech', 'RegTech', 'HealthTech & Wearables',
              'FoodTech', 'Climate Adaptation', 'Waste-to-Energy', 'Desalination',
              'EduTech', 'GovTech', 'LegalTech', 'Gaming', 'Music & Creative',
              'Film & Animation', 'Podcasting', 'Co-Working', 'Pet Care', 'Senior Care',
              'Fertility Health', 'Mental Health', 'Cannabis & Hemp', 'Luxury Goods',
              'Duty Free', 'Event Management', 'Heritage Tourism', 'Eco-Tourism',
              'Sports Tech', 'eSports', 'Vertical Farming', 'Aquaponics',
              'Forest Carbon', 'Ocean Economy', 'Geothermal', 'Tidal Energy',
              'Small Modular Reactors', 'Green Building', 'Industrial IoT',
              'Supply Chain Finance', 'Cross-Border Payments', 'Islamic Finance',
              'SWF Advisory', 'Infrastructure Bonds', 'Data Centers', 'Fiber Optics', '5G Networks',
              'Emission Monitoring', 'CEMS', 'Carbon Trading', 'Carbon Capture (CCS)',
              'Carbon Offsets', 'Methane Abatement', 'Air Quality Sensors', 'ESG Reporting',
              'Climate Risk Analytics', 'Sustainable Aviation Fuels', 'Green Hydrogen',
              'Battery Recycling', 'Circular Economy', 'Environmental Remediation', 'Biodiversity Credits'
            ].map((sector, i) => (
              <span key={i} className="text-[10px] font-medium text-[#004ac6] bg-[#e5eeff] px-2.5 py-1.5 rounded-lg">
                {sector}
              </span>
            ))}
          </div>
        </section>

        {/* Footer */}
        <div className="text-center py-8 text-[10px] text-[#434655]">
          Built on Emergent Platform — React + FastAPI + MongoDB + AI Intelligence Engine + World Bank API + IMF WEO + 1,000 Research Sources
        </div>
      </div>
      </div>
    </div>
  );
}

export default PlatformDocsPage;
